import {
  PageLayout,
  Typography,
  ButtonsWrapper,
  Button,
  ICONS,
  PLATFORM_THEME_COLORS,
  TYPOGRAPHY_SIZE,
  BoxExpand,
} from '@thinknet-gmbh/platform-common-ui';
import {
  PERMISSION_ACTIONS,
  PLATFORM_PERMISSIONS,
  ProjectStatusesEnum,
} from '@thinknet-gmbh/platform-common';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { useGetCustomerById } from 'src/hooks/customers/get-customer-by-id.hook';
import { useUserCanManageDocument } from 'src/hooks/user-can-manage-document.hook';
import { ROUTES } from 'src/components/app/app-routes.constants';
import { PROJECTS_SUB_PAGES } from 'src/pages/projects/sub-pages/create-project/create-project.constants';
import { useGetProjects } from 'src/hooks/projects/get-projects.hook';
import { useGetCurrentCompanyId } from 'src/hooks/companies/get-current-company-id.hook';
import PermissionCheck from 'src/components/permission-check';

import { CustomerDetailsEdit } from './components/customer-details-edit';
import { CustomerDetailsInfo } from './components/customer-details-info';
import { HeaderContainer } from './customer-details.styles';
import { CustomerRelatedProjects } from './components/customer-related-projects';

const CustomerDetails = () => {
  const { t } = useTranslation(['customers', 'common']);
  const navigate = useNavigate();
  const { id } = useParams();
  const [isEdit, setIsEdit] = useState(false);

  const currentCompanyId = useGetCurrentCompanyId();
  const { customer } = useGetCustomerById(id as string, currentCompanyId);
  const { projects, onTableChange, paginationConfig } = useGetProjects({
    customerId: customer._id,
    projectStatuses: [...Object.values(ProjectStatusesEnum)],
  });

  const { canEdit } = useUserCanManageDocument({
    createdBy: customer.createdBy || '',
    platformPermission: PLATFORM_PERMISSIONS.customerManagement,
  });

  return (
    <PageLayout pageTitle={t('labels.customerProfile')}>
      <HeaderContainer>
        <Typography weight="bold" size={TYPOGRAPHY_SIZE.medium}>
          {t('labels.customersDetails')}
        </Typography>
        <ButtonsWrapper>
          <PermissionCheck
            action={PERMISSION_ACTIONS.create}
            permission={PLATFORM_PERMISSIONS.projectManagement}
          >
            <Button
              type="primary"
              ghost
              icon={ICONS.plus}
              iconColor={PLATFORM_THEME_COLORS.primary.blue}
              onClick={() =>
                navigate(`${ROUTES.customers}/${PROJECTS_SUB_PAGES.createProject}/${id}`)
              }
            >
              {t('buttons.addNewProject')}
            </Button>
          </PermissionCheck>
          {!isEdit && canEdit && (
            <Button
              type="primary"
              ghost
              icon={ICONS.editAlt}
              iconColor={PLATFORM_THEME_COLORS.primary.blue}
              onClick={() => setIsEdit(true)}
            >
              {t('common:buttons.edit')}
            </Button>
          )}
        </ButtonsWrapper>
      </HeaderContainer>
      {isEdit ? (
        <CustomerDetailsEdit onCancelEdit={() => setIsEdit(false)} data={customer} />
      ) : (
        <CustomerDetailsInfo data={customer} />
      )}
      {!isEdit && (
        <BoxExpand
          title={t('labels.allRelatedProjects')}
          titleFontSize={TYPOGRAPHY_SIZE.medium}
          separator
        >
          <CustomerRelatedProjects
            dataSource={projects}
            pagination={paginationConfig}
            onChange={onTableChange}
          />
        </BoxExpand>
      )}
    </PageLayout>
  );
};

export default CustomerDetails;
